import React, { Fragment, useRef, useState, useEffect } from 'react';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';
import profilePlaceholder from '../../assets/images/profile/profile-placeholder.png';
import checkCirclePurpleLighterIcon from '../../assets/svg/check-circle-purple-lighter.svg';
import ellipsisHorizontalIcon from '../../assets/svg/ellipsis-h.svg';
import arrowDownIcon from '../../assets/svg/chevron-down-purple.svg';
import { ReactComponent as SendIcon } from '../../assets/svg/send-icon-white-v2.svg';
import pinIcon from '../../assets/svg/pin.svg';
import globeGreyIcon from '../../assets/images/project/globe-grey.svg';
import ArchiveProject from './Action/ArchiveProject';
import {
  GET_ALL_PROJECTS_getAllProjects_projects,
  GET_ALL_PROJECTS_getAllProjects_projects_collaborations
} from '../../graphql/types/GET_ALL_PROJECTS';
import { GET_CURRENT_USER_me } from '../../graphql/types/GET_CURRENT_USER';
import { getCompressedImagePath } from '../../utils/getCompressedImagePath';
import CommentForm from '../Comment/CommentForm';
import CommentList from '../Comment/CommentList';
import { CommentCategory } from '../Comment/constants';
import { PrettyContent } from '../Content';
import InterestedProject from './Action/Interested';
import LikeProject from './Action/LikeProject';
import ProjectTags from './ProjectTags';
import Video from './Video';
import ProjectShareModal from './ProjectShareModal';
const MAX_DESCRIPTION_LENGTH = 200;

const renderMediaGallery = (
  project: GET_ALL_PROJECTS_getAllProjects_projects,
  linkToProject: any
) => {
  if (project.videoUrl) {
    return <Video project={project} />;
  }
  if (!project.medias || project.medias.length === 0) {
    return null;
  }

  const mediaCount = project.medias.length;

  if (mediaCount === 1) {
    return (
      <Link to={linkToProject}>
        <div className="post-card-image-gallery post-card-image-gallery--single">
          <img
            src={project.medias[0].fileUrl}
            alt={project.title}
            className="post-card-gallery-image"
          />
        </div>
      </Link>
    );
  }
  if (mediaCount === 2) {
    return (
      <Link to={linkToProject}>
        <div className="post-card-image-gallery post-card-image-gallery--two">
          <img
            src={project.medias[0].fileUrl}
            alt={project.title}
            className="post-card-gallery-image"
          />
          <img
            src={project.medias[1].fileUrl}
            alt={project.title}
            className="post-card-gallery-image"
          />
        </div>
      </Link>
    );
  }
  return (
    <Link to={linkToProject}>
      <div className="post-card-image-gallery post-card-image-gallery--multiple">
        <div className="post-card-gallery-main">
          <img
            src={project.medias[0].fileUrl}
            alt={project.title}
            className="post-card-gallery-image"
          />
        </div>
        <div className="post-card-gallery-sidebar">
          <img
            src={project.medias[1].fileUrl}
            alt={project.title}
            className="post-card-gallery-image"
          />
          <img
            src={project.medias[2].fileUrl}
            alt={project.title}
            className="post-card-gallery-image"
          />
        </div>
      </div>
    </Link>
  );
};

export const ProjectFeed = ({
  project,
  user,
  commentFrom
}: {
  project: GET_ALL_PROJECTS_getAllProjects_projects;
  user: GET_CURRENT_USER_me;
  commentFrom?: CommentCategory;
}) => {
  const [shareModalIsOpen, setShareModalIsOpen] = useState(false);
  const linkToProject = {
    pathname: `/project/${project.id}`,
    state: window.location.pathname
  };

  const inputEl = useRef<HTMLInputElement>(null);
  const [collaborations, setCollaborations] = useState<
    GET_ALL_PROJECTS_getAllProjects_projects_collaborations[]
  >([]);

  const sortCollaboration = () => {
    if (project.collaborations && project.collaborations.length > 0) {
      const sortedCollaborations = project.collaborations
        .slice()
        .sort((collabA, collabB) => {
          const foundA = collabA.isFound ?? false;
          const foundB = collabB.isFound ?? false;
          return foundA.toString().localeCompare(foundB.toString()) ? 1 : 0;
        });
      setCollaborations(sortedCollaborations);
    }
  };

  const bgImage =
    project.author && project.author.avatar && project.author.avatar.fileUrl
      ? project.author.avatar.fileUrl
      : profilePlaceholder;

  const compressedAvatarImage = getCompressedImagePath(bgImage, 'thumbnail');

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(true);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown.is-active')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    sortCollaboration();
  }, [project.collaborations]);

  useEffect(() => {
    sortCollaboration();
  }, []);

  return (
    <div
      className="content post-card is-dropshadowed"
      key={`${project.id}-${project.title}`}
    >
      <div className="post-card-header">
        <div className="post-card-meta-row">
          <div className="post-card-type-section">
            <span className="post-card-type-label is-capitalized">
              {project.type && project.type.toLocaleLowerCase()} project
            </span>
          </div>

          <div className="post-card-header-actions">
            {project.author?.id === user?.id && (
              <span
                className="post-card-visibility-badge"
                title={project.isArchived
                  ? 'Archived - Only visible to you'
                  : 'Public - Visible to everyone'}
              >
                {project.isArchived ? (
                  <>
                    <span className="post-card-visibility-badge__icon-wrapper">
                      <img
                        src={globeGreyIcon}
                        alt="archived"
                      />
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 20 20"
                      >
                        <line
                          x1="2"
                          y1="18"
                          x2="18"
                          y2="2"
                          stroke="#9CA3AF"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </span>
                    <span>Archived</span>
                  </>
                ) : (
                  <>
                    <img
                      src={globeGreyIcon}
                      alt="public"
                      className="post-card-visibility-badge__icon"
                    />
                    <span className="post-card-visibility-badge__text">Public</span>
                  </>
                )}
              </span>
            )}
            {commentFrom === CommentCategory.userProjectListing && (
              <div className={`dropdown is-right ${isDropdownOpen && 'is-active'}`}>
                <div className="dropdown-trigger">
                  <img
                    src={ellipsisHorizontalIcon}
                    alt="ellipsis"
                    className="image is-inline post-card-dropdown-trigger"
                    aria-haspopup="true"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDropdownOpen(!isDropdownOpen);
                    }}
                  />
                </div>
                <div className="dropdown-menu" role="menu">
                  <div className="dropdown-content post-card-dropdown-content-wrapper">
                    {project.author?.id === user?.id && (
                      <ArchiveProject
                        projectId={project.id}
                        projectAuthorId={project.author?.id}
                        currentUserId={user?.id}
                        isArchived={project.isArchived || false}
                        onArchiveComplete={() => setIsDropdownOpen(false)}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="post-card-title-section">
          <Link to={linkToProject}>
            <h3 className="post-card-title">
              <span>{project.title}</span>
            </h3>
          </Link>
        </div>

        {(project.genres?.length > 0 || (project.locations && project.locations[0])) && (
          <div className="post-card-tags-row">
            <ProjectTags genres={project.genres} />
            {project.locations && project.locations[0] && (
              <span className="post-card-location-tag">
                <img src={pinIcon} alt="location" className="post-card-location-icon" />
                {project.locations[0].name}
              </span>
            )}
          </div>
        )}

        {project.createdAt && (
          <div className="post-card-updated">
            <span className="post-card-updated-text">
              last updated <Moment format="YYYY/MM/DD">{project.createdAt}</Moment>
            </span>
          </div>
        )}

        <div className="post-card-content">
          {project.description &&
            project.description.length > MAX_DESCRIPTION_LENGTH ? (
            <Fragment>
              <PrettyContent
                content={project.description.substr(0, MAX_DESCRIPTION_LENGTH) + '... '}
                isLineBreakEnabled={false}
              />
              <Link to={linkToProject} className="post-card-read-more">
                <span className="post-card-read-more-text">Read More</span>
                <img src={arrowDownIcon} alt="arrow down" className="post-card-read-more-icon" />
              </Link>
            </Fragment>
          ) : (
            <PrettyContent
              content={project.description}
              isLineBreakEnabled={false}
            />
          )}
        </div>
      </div>

      {renderMediaGallery(project, linkToProject)}

      <div className="post-card-engagement">
        <div className="post-card-engagement-actions">
          <LikeProject
            pageType={'project'}
            postId={project.id}
            likes={project.likes ? project.likes : []}
            user={user}
            wasLikedByUser={Boolean(
              user &&
                project.likes?.find((like) => like.author?.id === user.id)
            )}
          />
          <button
            className="post-card-action-button post-card-action-button--comment"
            onClick={() => setIsCommentsOpen(!isCommentsOpen)}
          >
            <div className="post-card-comment-icon-wrapper">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="post-card-action-icon">
                <path
                  d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {project.comments && project.comments.length > 0 && (
                <span className={`post-card-comment-badge ${isCommentsOpen ? 'is-active' : ''}`}></span>
              )}
            </div>
            {project.comments && project.comments.length > 0 && (
              <span className="post-card-action-count">{project.comments.length}</span>
            )}
          </button>
        </div>
        <div className="post-card-engagement-interested">
          <InterestedProject
            pageType={'project'}
            projectAuthorId={project.author?.id}
            currentUserId={user.id}
            projectId={project.id}
            user={user}
            interested={project.interested}
            wasInterestedByUser={
              project.interested &&
              Boolean(
                project.interested.find((itrs) => itrs.author.id === user.id)
              )
            }
          />
        </div>
      </div>

      <div className="post-card-author-section">
        <div className="post-card-author">
          <figure
            className="post-card-author-avatar"
            style={{
              backgroundImage: `url(${compressedAvatarImage || profilePlaceholder})`
            }}
          />
          <div className="post-card-author-info">
            <Link to={`/user/${project.author && project.author.id}`} className="post-card-author-name">
              {project.author && project.author.profile && project.author.profile.name}
            </Link>
            {project.author && project.author.professions && project.author.professions.length > 0 && (
              <p className="post-card-author-profession">
                {project.author.professions.map((profession, idx) => (
                  <span key={idx}>
                    {(idx ? ', ' : '') + profession.name}
                  </span>
                ))}
              </p>
            )}
          </div>
          <button
            className="post-card-author-message-button"
            onClick={() => setShareModalIsOpen(!shareModalIsOpen)}
            title="Share project"
          >
            <SendIcon className="post-card-send-icon" />
          </button>
          {shareModalIsOpen && (
            <div className={`modal ${shareModalIsOpen && 'is-active'}`}>
              <div
                className="modal-background"
                onClick={() => setShareModalIsOpen(false)}
              />
              <ProjectShareModal
                modalIsOpen={shareModalIsOpen}
                handleModalToggle={() => setShareModalIsOpen(!shareModalIsOpen)}
                project={project}
              />
            </div>
          )}
        </div>

        {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
        {collaborations.some(c => (c.requiredProfessions && c.requiredProfessions.length > 0) || (c.requiredSkills && c.requiredSkills.length > 0)) ? (
          <div className="post-card-looking-for">
            <div className="post-card-looking-for-content">
              <p className="post-card-looking-for-label">
                Looking for:
              </p>
              <div className="post-card-looking-for-roles">
                {project.collaborations?.map((collaboration) => {
                  const displayRoles = collaboration.requiredProfessions && collaboration.requiredProfessions.length > 0
                    ? collaboration.requiredProfessions
                    : collaboration.requiredSkills;

                  return (
                    <Fragment key={collaboration.id}>
                      {displayRoles &&
                        displayRoles.length > 0 &&
                        displayRoles.map(
                          (skill: any) => (
                            <span key={skill.id} className="post-card-role-tag">
                              <span className={collaboration.isFound ? 'is-found' : ''}>
                                {collaboration.isFound && (
                                  <img
                                    className="post-card-role-check"
                                    src={checkCirclePurpleLighterIcon}
                                    alt="check"
                                  />
                                )}
                                {skill.name}
                              </span>
                            </span>
                          )
                        )}
                    </Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="post-card-looking-for">
            <p>
              This project is currently <strong>not</strong> looking for
              collaborations.
            </p>
          </div>
        )}
      </div>

      {
        isCommentsOpen && (
          <div className="post-card-comments">
            <CommentList
              comments={project.comments}
              commentCategory={commentFrom}
              user={user}
            />
            <CommentForm
              ref={inputEl}
              projectId={project.id}
              user={user}
              author={project.author ?? undefined}
              commentFrom={commentFrom}
            />
          </div>
        )
      }
    </div>
  );
};
